/**
 * Phase 129 (Plan 129-10, D-15) — TanStack Query hooks for the Engineering tab's release flags.
 *
 * Query key: ['project-engineering', projectId, 'release-flags'] (see `projectEngineeringKey`).
 * Mutations are server-confirm only; each one refreshes the project's list, the SPA's own flag
 * values and the admin list.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchProjectReleaseFlags,
  resetProjectReleaseFlagOverride,
  switchProjectReleaseFlagOff,
  switchProjectReleaseFlagTicketOn,
} from '../services/api/projectReleaseFlags';
import type {
  AdminReleaseFlagRow,
  ReleaseFlagStateResult,
  ReleaseFlagSwitchOnResult,
} from '../services/api/releaseFlags';
import { projectEngineeringKey } from './useProjectEngineering';
import { ADMIN_RELEASE_FLAGS_QUERY_KEY, RELEASE_FLAGS_QUERY_KEY } from './useReleaseFlags';

function validId(projectId: number | undefined): projectId is number {
  return projectId !== undefined && Number.isFinite(projectId) && projectId > 0;
}

export function useProjectReleaseFlags(projectId: number | undefined) {
  return useQuery<AdminReleaseFlagRow[]>({
    queryKey: projectEngineeringKey(projectId, 'release-flags'),
    queryFn: () => fetchProjectReleaseFlags(projectId as number),
    enabled: validId(projectId),
    refetchOnWindowFocus: true,
  });
}

function useInvalidateAfterFlagAction(projectId: number) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: projectEngineeringKey(projectId, 'release-flags') });
    // The person's own flag values, so a gated feature appears or disappears without a reload
    // (D-15), and the admin list, which stays fresh while the tab's flag is toggled.
    queryClient.invalidateQueries({ queryKey: RELEASE_FLAGS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ADMIN_RELEASE_FLAGS_QUERY_KEY });
  };
}

export function useSwitchProjectFlagOff(projectId: number) {
  const invalidate = useInvalidateAfterFlagAction(projectId);
  return useMutation<ReleaseFlagStateResult, Error, string>({
    mutationFn: (key: string) => switchProjectReleaseFlagOff(projectId, key),
    onSuccess: invalidate,
  });
}

export function useResetProjectFlagOverride(projectId: number) {
  const invalidate = useInvalidateAfterFlagAction(projectId);
  return useMutation<ReleaseFlagStateResult, Error, string>({
    mutationFn: (key: string) => resetProjectReleaseFlagOverride(projectId, key),
    onSuccess: invalidate,
  });
}

/** "Switch on (mark verified)" for a ticket of this project. */
export function useSwitchProjectTicketOn(projectId: number) {
  const invalidate = useInvalidateAfterFlagAction(projectId);
  return useMutation<ReleaseFlagSwitchOnResult, Error, string>({
    mutationFn: (ticket: string) => switchProjectReleaseFlagTicketOn(projectId, ticket),
    onSuccess: invalidate,
  });
}
